import React, { useContext, useState, useEffect, useRef } from 'react'
import getConfig from 'next/config'
import Head from 'next/head'
import ProductTabs, { PRODUCT_TABS } from '../../components/ProductTabs'
import VariantPicker from '../../components/VariantPicker'
import ShopSwitchPicker from '../../components/ShopSwitchPicker'
import SocialShare from '../../components/SocialShare'
import Context from '../../context'
import MainCarousel from '../../components/MainCarousel'
import ContentGroup from '../../components/ContentGroup'
import BreadCrumb from '../../components/BreadCrumb'
import PDPFulfillment from '../../components/PDPFulfillment'
import { useAuthAPI } from '../../hooks/api.hooks'
import SVG from '../../components/SVG'
import { useIonModal } from '@ionic/react'
import RecentlyViewed from '../../components/RecentlyViewed'
import Widget from '../../components/Widget'
// import { getCookie } from '../../utils/writeCookie'
import { formatF1String, setProductViewData, DataLayerProduct, setPageViewData } from '../../utils/analytics'
import { fireBVPageViewEvent, getBVScriptUrl, executeWhenBVReady } from '../../utils/bazaarvoice'
import ProductPriceWithUnit from '../../components/ProductPriceWithUnit'

const { publicRuntimeConfig } = getConfig()

const Product = ({
  product,
  fullUrl,
  slug,
  breadCrumb,
  widget
}) => {
  const { dispatch, state } = useContext(Context)
  const [productDetail, setProductDetail] = useState([...product])
  const [optionsSize, setOptionsSize] = useState(-1)
  const [selectedOptions, setSelectedOptions] = useState<any>({})
  const [selectedProduct, setSelectedProduct] = useState<any>(
    product.find((variant) => variant.slug === slug)
  )
  const [hideReviews, setHideReviews] = useState(false)
  const [productTabIndex, setProductTabIndex] = useState(0)
  const authAPICall = useAuthAPI()
  const isBRLoaded = useRef(false)
  const isBVPixelFired = useRef(false)
  const [bvToken, setBvToken] = useState<string | null>(null)
  const [isBVReady, setIsBVReady] = useState(false)

  const bvProductdId = selectedProduct.dg_root_sv
  const title = productDetail[0].digital_product_name || productDetail[0].product_name

  useEffect(() => {
    if (product[0]?.sku !== productDetail[0]?.sku) {
      setProductDetail([...product])
      setSelectedProduct(product.find((variant) => variant.slug === slug))
    }
  }, [product])

  useEffect(() => {
    if (typeof window !== 'undefined' && Object.keys(selectedProduct).length && !isBRLoaded.current) {
      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.ptype = 'product'
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.prod_id = product[0].digital_sku
      window.br_data.prod_name = selectedProduct.product_name
      window.br_data.sku = selectedProduct.sku
      window.br_data.user_id = state.user.UID || state.deviceId
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE;

      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID
          }.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
        isBRLoaded.current = true
      })()
    }
  }, [selectedProduct])

  useEffect(() => {
    if (selectedProduct?.sku && !isBVPixelFired.current && bvProductdId) {
      const categoryId = breadCrumb && breadCrumb.length > 0
        ? breadCrumb[breadCrumb.length - 1]?.category_id || breadCrumb[breadCrumb.length - 1]?.slug
        : undefined

      fireBVPageViewEvent({
        bvProduct: 'RatingsAndReviews',
        productId: bvProductdId,
        brand: 'Popshelf',
        type: 'Product',
        categoryId: categoryId
      })
      isBVPixelFired.current = true
    }
  }, [selectedProduct, productDetail])

  useEffect(() => {
    const initBazaarvoiceAuth = async () => {
      if (selectedProduct) {
        let token = null
        if (state.user?.UID && state.user?.email && state.user?.authType !== 0) {
          const tokenData = await authAPICall('user', 'bvtoken', 'POST', {
          }, {
            userEmail: state.user.email
          }, {
            customerid: state.user.UID
          })
          token = tokenData?.UAS
        }

        setBvToken(token)
        setIsBVReady(true)
      }
    }

    initBazaarvoiceAuth()
  }, [selectedProduct, state.user])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedItems = state.recentlyViewed
      const index = storedItems.findIndex(
        (e) => e.sku === productDetail[0].sku
      )
      if (index !== -1) {
        const del = storedItems.splice(index, 1)
        del[0].price = productDetail[0].price
        del[0].store = state.cart.store?.storenumber
        storedItems.unshift(del[0])
      } else {
        storedItems.unshift({
          title: productDetail[0].product_name,
          time: new Date(),
          sku: productDetail[0].sku,
          upc: productDetail[0].upc,
          image: productDetail[0].image_urls
            ? productDetail[0].image_urls.split(',')[0]
            : null,
          slug: productDetail[0].slug,
          avg_rating: productDetail[0].avg_rating,
          price: productDetail[0].price,
          store: state.cart.store?.storenumber
        })

        if (storedItems.length > 10) {
          storedItems.length = 10
        }
      }
      dispatch({ type: 'SET_RECENTLY_VIEWED', payload: storedItems })
    }
  }, [])

  useEffect(() => {
    const pageViewProductString = `${formatF1String(productDetail[0].product_name)}|${productDetail[0].upc}`
    const products: DataLayerProduct[] = [{
      productInfo: {
        name: productDetail[0].product_name,
        productID: productDetail[0].upc
      }
    }]

    setPageViewData(pageViewProductString, 'PDP', 'PDP')
    setProductViewData(products)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).appEventData = (window as any).appEventData || []
      setHideReviews(false)
    }
  }, [])

  useEffect(() => {
    executeWhenBVReady((BV) => {
      if (BV.swat_reviews) {
        BV.swat_reviews.on('show', () => {
          setProductTabIndex(PRODUCT_TABS.REVIEWS)
        })
      }
    })
  }, [])

  useEffect(() => {
    if (Object.keys(selectedOptions).length === optionsSize) {
      const foundSelectedProd = productDetail.find((prod) => {
        let found = true
        for (const attr in selectedOptions) {
          if (`${prod[attr]}` !== `${selectedOptions[attr]}`) {
            found = false
            break
          }
        }
        return found
      })

      foundSelectedProd
        ? setSelectedProduct(foundSelectedProd)
        : setSelectedProduct({ ...selectedProduct, qtyOnHand: 0 })
    }
  }, [selectedOptions])

  useEffect(() => {
    setSelectedProduct(
      productDetail.find((item) => +item.upc === +selectedProduct.upc) ||
      selectedProduct
    )
  }, [productDetail])

  useEffect(() => {
    const getProduct = async () => {
      const prod = await authAPICall(
        'productinfo',
        'product',
        'GET',
        {
          slug: product[0].slug,
          store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
          popkey: publicRuntimeConfig.POP_KEY
        },
        {}
      )
      setProductDetail(prod)
    }

    getProduct()
  }, [state.cart.store, product])

  const getDropDownValues = (value, productDetail) => {
    const uniqueLetters = [...new Set(productDetail.map(x => x[value]))]
    const letteroptions = []
    const altOptions = Object.keys(selectedOptions)
    const sizesOrder = {
      Small: 0,
      Medium: 1,
      Large: 2,
      'X Large': 3,
      '2X Large': 4
    }
    const arrayToObject = (array) => {
      array.forEach(element => {
        const currentAltSelection = altOptions.filter(x => x !== value)
        if (productDetail.some(x => x.qtyOnHand > 0 && x[value] === element && (altOptions.length < 2 || !altOptions.includes(value) || (altOptions.includes(value) && currentAltSelection.length > 0 && x[currentAltSelection[0]] === selectedOptions[currentAltSelection[0]])))) {
          letteroptions.push({ value: element, number: element })
        } else {
          letteroptions.push({ value: element, number: element + ' - unavailable' })
        }
      })
    }
    if (Object.keys(sizesOrder).some(item => uniqueLetters.includes(item))) {
      const orderedSizes = uniqueLetters.sort((a: string, b: string) => sizesOrder[a] - sizesOrder[b])
      arrayToObject(orderedSizes)
      return letteroptions
    } else {
      arrayToObject(uniqueLetters)
      return letteroptions.sort((a, b) => (a.value > b.value) ? 1 : -1)
    }
  }

  const handleDismiss = () => {
    dismiss()
  }
  const [present, dismiss] = useIonModal(SocialShare, {
    handleDismiss,
    pageType: 'Product',
    item: selectedProduct,
    image: selectedProduct.image_urls,
    title: selectedProduct.product_name,
    description: selectedProduct.description || ''
  })

  useEffect(() => {
    const initialProduct = productDetail.find((e) => e.slug === slug)
    const obj: any = {}
    for (const dropdown in initialProduct?.dropdown_attr) {
      switch (dropdown) {
        case 'Metal':
          obj.metal = initialProduct.metal
          break
        case 'Plating':
          obj.plating = initialProduct.plating
          break
        case 'Pattern':
          obj.pattern = initialProduct.pattern
          break
        case 'Finish':
          obj.finish = initialProduct.finish
          break
        case 'Scent':
          obj.scent = initialProduct.scent
          break
        case 'Color':
          obj.color = initialProduct.color
          break
        case 'Diaper Size':
          obj.diaper_size = initialProduct.diaper_size
          break
        case 'Baby Clothing Size':
          obj.baby_clothing_size = initialProduct.baby_clothing_size
          break
        case 'Count':
          obj.count = initialProduct.count
          break
        case 'Size':
          obj.size = initialProduct.size
          break
        case 'Letter':
          obj.letter = initialProduct.letter
          break
        case 'Number':
          obj.number = initialProduct.number
          break
      }
    }
    setSelectedOptions({ ...obj })
  }, [])

  const renderOptions = () => {
    const arr = []
    if (productDetail[0].dropdown_attr) {
      if (productDetail[0].dropdown_attr.Metal) {
        const options = getDropDownValues('metal', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Metal"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, metal: choice })
            }
            value={selectedOptions.metal}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Plating) {
        const options = getDropDownValues('plating', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Plating"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, plating: choice })
            }
            value={selectedOptions.plating}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Pattern) {
        const options = getDropDownValues('pattern', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Pattern"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, pattern: choice })
            }
            value={selectedOptions.pattern}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Finish) {
        const options = getDropDownValues('finish', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Finish"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, finish: choice })
            }
            value={selectedOptions.finish}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Scent) {
        const options = getDropDownValues('scent', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Scent"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, scent: choice })
            }
            value={selectedOptions.scent}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Color) {
        const options = [...new Set(productDetail.map((item) => item.color))]
        const altOption = Object.keys(selectedOptions).filter(
          (option) => option !== 'color'
        )[0]
        const colorImages = [
          ...new Set(
            productDetail.map((item) => {
              const returnImage = {
                color: item.color,
                images: item.image_urls,
                isAvailable: item.qtyOnHand > 0
              }
              if (altOption) {
                returnImage[altOption] = item[altOption]
              }
              return returnImage
            })
          )
        ]
        if (options.length > 1) {
          arr.push(
          <ShopSwitchPicker
            name="Color"
            data={options}
            product={selectedProduct}
            images={colorImages}
            altOption={altOption}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, color: choice })
            }
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr['Diaper Size']) {
        const options = getDropDownValues('diaper_size', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Diaper Size"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, diaper_size: choice })
            }
            value={selectedOptions.diaper_size}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr['Baby Clothing Size']) {
        const options = getDropDownValues('baby_clothing_size', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Baby Clothing Size"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({
                ...selectedOptions,
                baby_clothing_size: choice
              })
            }
            value={selectedOptions.baby_clothing_size}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Count) {
        const options = getDropDownValues('count', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Count"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, count: choice })
            }
            value={selectedOptions.count}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Size) {
        const options = getDropDownValues('size', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Size"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, size: choice })
            }
            value={selectedOptions.size}
          />
          )
        }
      }
      if (productDetail[0].dropdown_attr.Letter) {
        const letteroptions = getDropDownValues('letter', productDetail)
        if (letteroptions.length > 1) {
          arr.push(<VariantPicker
          name='Letter'
          data={letteroptions}
          onSelect={(choice) =>
            setSelectedOptions({ ...selectedOptions, letter: choice })
          }
          value={selectedOptions.letter}
        />)
        }
      }
      if (productDetail[0].dropdown_attr.Number || productDetail[0].dropdown_attr.Number === 0) {
        const options = getDropDownValues('number', productDetail)
        if (options.length > 1) {
          arr.push(
          <VariantPicker
            name="Number"
            data={options}
            onSelect={(choice) =>
              setSelectedOptions({ ...selectedOptions, number: choice })
            }
            value={selectedOptions.number}
          />
          )
        }
      }
    }
    optionsSize === -1 && setOptionsSize(arr.length)
    return arr
  }

  const renderPrice = () => {
    return (
      <div className="product-details__info-price">
        <ProductPriceWithUnit
          discountPrice={selectedProduct.discount_price}
          price={selectedProduct.price}
          unitDetails={selectedProduct?.unit_details}
        />
        {!!selectedProduct?.upc && (
          <button
            role='button'
            className="share"
            aria-label="share"
            onClick={() => present()}
          >
            <SVG name="share" label="share" size="md" />
          </button>
        )}
      </div>
    )
  }

  return (
    <div id="main-content" className="product-details">
      <Head>
        <title>{title}</title>
        <meta
          name="description"
          content={
            productDetail[0].description && productDetail[0].description.length > 150
              ? `${productDetail[0].description.slice(0, 147)}...`
              : productDetail[0].description || ''
          }
        />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={productDetail[0].product_name} />
        <meta property="og:description" content={productDetail[0].product_name} />
        <meta property="og:image" content={productDetail[0].image_urls} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              '@id': `https://${fullUrl}`,
              name: title,
              image: productDetail[0].image_urls?.split(',') || [],
              description: productDetail[0].description || '',
              sku: selectedProduct.sku
            })
          }}
        />

        {isBVReady && (
          <>
            {bvToken && <meta name="bv:userToken" content={bvToken} />}
            {getBVScriptUrl() && (
              <script
                type="text/javascript"
                async
                src={getBVScriptUrl()}
              />
            )}
          </>
        )}
      </Head>
      <section className="product-details__wrapper">
        <nav aria-label="Breadcrumb" className="nav__breadcrumb">
          <BreadCrumb props={breadCrumb} />
        </nav>
        <div className="product-details__container">
          <div className="product-details__images">
            <div className="regular-product-hero height__full">
              <div className="pdp-carousel height__full">
                <MainCarousel imageUrls={selectedProduct?.image_urls} />
              </div>
            </div>
          </div>

          <div className="product-details__info">
            <h1>{title}</h1>
            {!!selectedProduct?.sub_title && <p className="pop-sub-title-secondary">{selectedProduct?.sub_title}</p>}
            {!hideReviews && (
              <div className="product-details__info-reviews">
                <div data-bv-show="rating_summary" data-bv-product-id={bvProductdId}></div>
              </div>
            )}
            {renderPrice()}
            {productDetail.length > 1 && renderOptions()}
            <PDPFulfillment selectedProduct={selectedProduct} closeQuickViewModal={false} />
          </div>
        </div>
        <div className="product-details__extra-info">
          <ProductTabs data={selectedProduct} activeTabIndex={productTabIndex} onTabChange={setProductTabIndex} />
        </div>
        <div id="target-content-bottom">
          <ContentGroup />
        </div>
        <div className='pdp__recent-view'>
          <RecentlyViewed />
        </div>
        <div>
          <Widget type="p" slug={selectedProduct.slug} data={widget} />
        </div>
      </section>
    </div>
  )
}

export default Product

export const getServerSideProps = async ({ query, req }) => {
  const { publicRuntimeConfig } = getConfig()
  const fullUrl = req
    ? req.headers.host + req.url
    : window.location.protocol +
    '//' +
    window.location.hostname +
    (window.location.port ? ':' + window.location.port : '')

  const prodRes = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/product?slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )

  const product = await prodRes.json()

  if (!Object.keys(product).length) {
    return {
      notFound: true
    }
  }

  const breadCrumbRes = await fetch(
      `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/productBreadcrumb?slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}`,
      {
        headers: {
          deviceid: ''
        }
      }
  )
  const breadCrumb = await breadCrumbRes.json()

  const widgetDataRes = await fetch(
      `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/recommended?slug=${query.slug}&type=p&store=${publicRuntimeConfig.DEFAULT_STORE}&popkey=${publicRuntimeConfig.POP_KEY}`,
      {
        headers: {
          deviceid: ''
        }
      }
  )
  const widgetData = await widgetDataRes.json()

  return {
    props: {
      product: product || {},
      fullUrl,
      slug: query.slug,
      breadCrumb: breadCrumb,
      widget: widgetData.body.data,
      key: query.slug
    }
  }
}
